/**
 * Product Data Retrieval Module
 * 
 * This module handles the retrieval and caching of complete product data from the Shopify theme.
 * It fetches paginated product data in batches of 250 products and accumulates them into a
 * complete array. The data is cached in localStorage for 10 minutes to improve performance
 * and reduce server requests.
 * 
 * @author Pepper Lee Theme
 */

(async function() {
  'use strict';
  
  // Prevent multiple initializations if the module is already loaded
  if (typeof window.productData != 'undefined') {
    return;
  }

  /**
   * Saves product data to localStorage with expiration timestamp
   * 
   * @param {Array} productData - Array of product objects to store
   * @returns {void}
   */
  function saveToStorage(productData) {
    // Check if localStorage is available
    if (typeof localStorage === 'undefined') {
      return;
    }

    try {
      const now = new Date();
      
      // Store data with expiry timestamp (10 minutes from now)
      localStorage.setItem(
        'productData',
        JSON.stringify(
          {
            "data": productData,
            "expiry": now.getTime() + 600000 // 600,000ms = 10 minutes
          }
        )
      );
      
      // Verify that the data was stored successfully
      const storedData = localStorage.getItem('productData');
      if (!storedData) {
        console.warn('Product data was not stored successfully in localStorage');
        return;
      }
    } catch (error) {
      // If storage fails, log warning but don't halt execution
      console.warn('Failed to save product data to localStorage:', error);
    }
  }

  /**
   * Retrieves product data from localStorage if it exists and hasn't expired
   * 
   * @returns {Array} Array of product data or empty array if no valid data exists
   */
  function getFromStorage() {
    // Check if localStorage is available
    if (typeof localStorage === 'undefined') {
      return [];
    }

    try {
      // Attempt to retrieve stored data
      const storedJson = localStorage.getItem('productData');
      if (!storedJson) {
        return [];
      }

      const stored = JSON.parse(storedJson);
      
      // Validate the stored data structure
      if (!stored || typeof stored !== 'object' || !Array.isArray(stored.data) || typeof stored.expiry !== 'number') {
        console.warn('Invalid product data structure in localStorage, clearing corrupted data');
        localStorage.removeItem('productData');
        return [];
      }

      // Check if stored data has expired
      const now = new Date();
      if (stored.expiry < now.getTime()) {
        return [];
      }

      return stored.data;
    } catch (error) {
      // If any error occurs, clear corrupted data and return empty array
      console.warn('Error retrieving product data from localStorage:', error);
      return [];
    }
  }

  /**
   * Fetches a single page of product data from the Shopify theme
   * 
   * @param {number} page - Page number to fetch (1-based indexing)
   * @returns {Promise<Array>} Promise that resolves to array of product objects
   * @throws {Error} If the fetch request fails
   */
  async function fetchProductData(page) {
    try {
      const response = await fetch('/pages/product-data?page=' + page);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error(`Failed to fetch product data for page ${page}:`, error);
      return []; // Return empty array on error to prevent infinite loops
    }
  }

  // Initialize product data array from localStorage cache
  let productData = getFromStorage();

  // If we have valid cached data, use it and exit early
  if (productData.length) {
    window.productData = productData;
    return;
  }

  // Initialize pagination variables
  let pageCounter = 0;
  let batch = [null]; // Initialize with non-empty array to start the loop
  
  // Fetch all product data pages until we receive an empty batch
  // Each batch contains up to 250 products (Shopify's current pagination limit)
  while (batch.length) {
    pageCounter++;
    batch = await fetchProductData(pageCounter);
    
    // Concatenate the new batch with existing data
    productData = productData.concat(batch);
  }

  // Cache the complete product data array
  saveToStorage(productData);

  // Make the complete product data available globally
  window.productData = productData;
})();
